import * as React from 'react';
import styled, { keyframes } from 'styled-components';
import { Typography, Icon, Button } from '@mindlercare/ui-web';
import { defaultTheme } from '@mindlercare/ui-web/src/shared/constants';
import type { ExternalSources } from '../types';
import { useTranslation } from '../translations/TranslationHook';

interface AllSourcesPanelProps {
    sources: ExternalSources;
    onClose: () => void;
}

const fadeIn = keyframes`
    from {
        opacity: 0;
        transform: scale(0.95);
    }
    to {
        opacity: 1;
        transform: scale(1);
    }
`;

const AllSourcesPanelContainer = styled.div`
    height: 100%;
    width: calc(100% - ${defaultTheme.BORDER_RADIUS.LG});
    background: ${defaultTheme.COLORS.WHITE};
    border: 1px solid ${defaultTheme.COLORS.GRAY_MEDIUM};
    border-radius: ${defaultTheme.BORDER_RADIUS.MD};
    padding-top: ${defaultTheme.BORDER_RADIUS.LG};
    padding-bottom: ${defaultTheme.BORDER_RADIUS.LG};
    margin-left: ${defaultTheme.BORDER_RADIUS.LG};
    display: flex;
    flex-direction: column;
    animation: ${fadeIn} 0.2s ease-in-out;
`;

const PanelHeader = styled.div({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: defaultTheme.SPACING.MD,
    paddingRight: defaultTheme.SPACING.MD,
    background: defaultTheme.COLORS.WHITE,
    marginBottom: defaultTheme.SPACING.MD,
});

const PanelContent = styled.div({
    flex: 1,
    overflowY: 'auto',
    display: "flex",
    flexDirection: "column",
    gap: defaultTheme.SPACING.MD,
    padding: defaultTheme.SPACING.MD,
    paddingTop: 0,
});

const SourceItem = styled.div({
    padding: defaultTheme.SPACING.MD,
    background: defaultTheme.COLORS.GRAY_X_LIGHT,
    borderRadius: defaultTheme.BORDER_RADIUS.SM,
    display: 'flex',
    flexDirection: 'column',
    gap: defaultTheme.SPACING.LG,
});

const SourceTitle = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: defaultTheme.SPACING.SM,
});

const SourceTitleText = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: defaultTheme.SPACING.XS,
    flex: 1,
});

const IconLinkButton = styled.a({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    padding: 0,
    background: defaultTheme.COLORS.WHITE,
    border: `1px solid ${defaultTheme.COLORS.GRAY_MEDIUM}`,
    borderRadius: defaultTheme.BORDER_RADIUS.SM,
    cursor: 'pointer',
    textDecoration: 'none',
    color: defaultTheme.COLORS.GRAY_BLACK,
    transition: 'all 0.2s ease',

    '&:hover': {
        background: defaultTheme.COLORS.GRAY_LIGHT,
        borderColor: defaultTheme.COLORS.GRAY,
    },

    '& svg': {
        width: '16px',
        height: '16px',
    },
});

const SourceLinksContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: defaultTheme.SPACING.SM,
});

const SourceLink = styled.a({
    display: 'inline-flex',
    alignItems: 'center',
    gap: defaultTheme.SPACING.XS,
    padding: `${defaultTheme.SPACING.SM} ${defaultTheme.SPACING.MD}`,
    fontSize: defaultTheme.FONT.SIZE.SMALL,
    color: defaultTheme.COLORS.BLACK,
    background: defaultTheme.COLORS.WHITE,
    border: `1px solid ${defaultTheme.COLORS.BLACK}`,
    borderRadius: defaultTheme.BORDER_RADIUS.LG,
    cursor: 'pointer',
    textDecoration: 'none',
    transition: 'all 0.2s ease',
    width: 'fit-content',

    '&:hover': {
        background: defaultTheme.COLORS.WARM_LIGHT,
    },

    '& svg': {
        width: '14px',
        height: '14px',
    },
});

export const AllSourcesPanel: React.FC<AllSourcesPanelProps> = ({ sources, onClose }) => {
    const t = useTranslation();

    return (
        <AllSourcesPanelContainer>
            <PanelHeader>
                <Typography variant="h4">{t.ALL_SOURCES_PANEL_TITLE}</Typography>
                <Button variant="icon" onClick={onClose}>
                    <Icon.Close />
                </Button>
            </PanelHeader>
            <PanelContent>
                {sources && sources.length > 0 ? (
                    sources.map((source, index) => (
                        <SourceItem key={`${source.uri}-${index}`}>
                            <SourceTitle>
                                <SourceTitleText>
                                    <Typography variant='h5'>{source.displayName || source.uri}</Typography>
                                </SourceTitleText>
                            </SourceTitle>

                            {source.externalUrl && (
                                <SourceLink
                                    href={source.externalUrl}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                >
                                    <Icon.ExternalLink />
                                    <Typography style={{ marginLeft: defaultTheme.SPACING.XS }} variant="body-small">{t.EXTERNAL_LINK_LABEL}</Typography>
                                </SourceLink>
                            )}

                            {source.updateTime && (
                                <Typography variant='body-x-small' style={{ color: defaultTheme.COLORS.GRAY_DARK }}>
                                    {t.UPDATED_AT}: {new Date(source.updateTime).toLocaleString()}
                                </Typography>
                            )}
                        </SourceItem>
                    ))
                ) : (
                    <Typography variant="body" style={{ color: defaultTheme.COLORS.GRAY_DARK, textAlign: 'center', marginTop: defaultTheme.SPACING.LG }}>
                        {t.NO_SOURCES_MESSAGE}
                    </Typography>
                )}
            </PanelContent>
        </AllSourcesPanelContainer>
    );
};
